import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import smsService from '../services/smsService';

/**
 * SMS Marketing Dashboard
 * 
 * Comprehensive SMS marketing management including:
 * - SMS campaign CRUD
 * - Template management
 * - Recipient management
 * - Analytics and reporting
 */

const SMS_STATUS_COLORS = {
  draft: '#6b7280',
  scheduled: '#3b82f6',
  sending: '#f59e0b',
  sent: '#10b981',
  failed: '#ef4444',
  cancelled: '#9ca3af'
};

const TEMPLATE_CATEGORIES = [
  { id: 'promotional', name: 'Promotional', icon: '🏷️' },
  { id: 'transactional', name: 'Transactional', icon: '📦' },
  { id: 'reminder', name: 'Reminder', icon: '⏰' },
  { id: 'welcome', name: 'Welcome', icon: '👋' },
  { id: 'cart-abandonment', name: 'Cart Abandonment', icon: '🛒' },
  { id: 'feedback', name: 'Feedback Request', icon: '⭐' }
];

const SMSMarketing = () => {
  // State
  const [activeTab, setActiveTab] = useState('campaigns');
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [creditsBalance, setCreditsBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal states
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Form states
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    message: '',
    template_id: '',
    recipients_type: 'all',
    recipients_segment: '',
    scheduled_at: '',
    personalization: true
  });

  const [templateForm, setTemplateForm] = useState({
    name: '',
    category: 'promotional',
    content: '',
    variables: []
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [campaignsData, templatesData, analyticsResp, creditsResp] = await Promise.all([
        smsService.getCampaigns().catch(() => ({ campaigns: [] })),
        smsService.getTemplates().catch(() => ({ templates: [] })),
        smsService.getAnalyticsOverview().catch(() => null),
        smsService.getCreditsBalance().catch(() => ({ balance: 0 }))
      ]);

      setCampaigns(campaignsData.campaigns || []);
      setTemplates(templatesData.templates || []);
      setAnalyticsData(analyticsResp);
      setCreditsBalance(creditsResp);
    } catch (err) {
      console.error('Error fetching SMS data:', err);
      setError('Failed to load SMS marketing data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Create campaign
  const handleCreateCampaign = async () => {
    setLoading(true);
    try {
      await smsService.createCampaign(campaignForm);
      setShowCampaignModal(false);
      setSuccess('SMS campaign created successfully');
      setCampaignForm({
        name: '',
        message: '',
        template_id: '',
        recipients_type: 'all',
        recipients_segment: '',
        scheduled_at: '',
        personalization: true
      });
      fetchData();
    } catch (err) {
      setError('Failed to create SMS campaign');
    } finally {
      setLoading(false);
    }
  };

  // Send campaign
  const handleSendCampaign = async (campaignId) => {
    setLoading(true);
    try {
      await smsService.sendCampaign(campaignId);
      setSuccess('SMS campaign sent successfully');
      fetchData();
    } catch (err) {
      setError('Failed to send SMS campaign');
    } finally {
      setLoading(false);
    }
  };

  // Schedule campaign
  const handleScheduleCampaign = async (campaignId, scheduledTime) => {
    setLoading(true);
    try {
      await smsService.scheduleCampaign(campaignId, scheduledTime);
      setSuccess('SMS campaign scheduled successfully');
      fetchData();
    } catch (err) {
      setError('Failed to schedule SMS campaign');
    } finally {
      setLoading(false);
    }
  };

  // Delete campaign
  const handleDeleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    
    setLoading(true);
    try {
      await smsService.deleteCampaign(campaignId);
      setSuccess('SMS campaign deleted');
      fetchData();
    } catch (err) {
      setError('Failed to delete SMS campaign');
    } finally {
      setLoading(false);
    }
  };

  // Create template
  const handleCreateTemplate = async () => {
    setLoading(true);
    try {
      await smsService.createTemplate(templateForm);
      setShowTemplateModal(false);
      setSuccess('SMS template created successfully');
      setTemplateForm({
        name: '',
        category: 'promotional',
        content: '',
        variables: []
      });
      fetchData();
    } catch (err) {
      setError('Failed to create SMS template');
    } finally {
      setLoading(false);
    }
  };

  // Delete template
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    setLoading(true);
    try {
      await smsService.deleteTemplate(templateId);
      setSuccess('SMS template deleted');
      fetchData();
    } catch (err) {
      setError('Failed to delete SMS template');
    } finally {
      setLoading(false);
    }
  };

  // Calculate character count and segments
  const calculateSMSMetrics = (text) => {
    const charCount = text.length;
    const segments = Math.ceil(charCount / 160) || 1;
    return { charCount, segments };
  };

  // Render stats cards
  const renderStatsCards = () => (
    <div className="stats-grid-4">
      <div className="stat-card">
        <div className="stat-icon">💬</div>
        <div className="stat-content">
          <div className="stat-value">{analyticsData?.total_sent?.toLocaleString() || '0'}</div>
          <div className="stat-label">Messages Sent</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">✅</div>
        <div className="stat-content">
          <div className="stat-value">{analyticsData?.delivery_rate || '0'}%</div>
          <div className="stat-label">Delivery Rate</div>
        </div>
      </div>
      <div className="stat-card">
        <div className="stat-icon">👆</div>
        <div className="stat-content">
          <div className="stat-value">{analyticsData?.click_rate || '0'}%</div>
          <div className="stat-label">Click Rate</div>
        </div>
      </div>
      <div className="stat-card credits">
        <div className="stat-icon">💰</div>
        <div className="stat-content">
          <div className="stat-value">{creditsBalance?.balance?.toLocaleString() || '0'}</div>
          <div className="stat-label">SMS Credits</div>
        </div>
      </div>
    </div>
  );

  // Render campaign card
  const renderCampaignCard = (campaign) => {
    const { charCount, segments } = calculateSMSMetrics(campaign.message || '');
    
    return (
      <div key={campaign.id} className="campaign-card">
        <div className="campaign-header">
          <div className="campaign-info">
            <h4 className="campaign-name">{campaign.name}</h4>
            <span className="campaign-date">
              {campaign.scheduled_at 
                ? `Scheduled: ${new Date(campaign.scheduled_at).toLocaleString()}`
                : `Created: ${new Date(campaign.created_at).toLocaleDateString()}`
              }
            </span>
          </div>
          <div 
            className="campaign-status"
            style={{ background: SMS_STATUS_COLORS[campaign.status] }}
          >
            {campaign.status}
          </div>
        </div>

        <div className="campaign-message">
          <p>{campaign.message?.substring(0, 160)}{campaign.message?.length > 160 ? '...' : ''}</p>
          <div className="message-meta">
            <span>{charCount} characters</span>
            <span>{segments} segment{segments > 1 ? 's' : ''}</span>
          </div>
        </div>

        {campaign.status === 'sent' && (
          <div className="campaign-stats">
            <div className="stat">
              <span className="stat-value">{campaign.recipients_count?.toLocaleString() || 0}</span>
              <span className="stat-label">Recipients</span>
            </div>
            <div className="stat">
              <span className="stat-value">{campaign.delivered_count?.toLocaleString() || 0}</span>
              <span className="stat-label">Delivered</span>
            </div>
            <div className="stat">
              <span className="stat-value">{campaign.clicked_count || 0}</span>
              <span className="stat-label">Clicked</span>
            </div>
            <div className="stat">
              <span className="stat-value">${campaign.cost?.toFixed(2) || '0.00'}</span>
              <span className="stat-label">Cost</span>
            </div>
          </div>
        )}

        <div className="campaign-actions">
          {campaign.status === 'draft' && (
            <>
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => handleSendCampaign(campaign.id)}
              >
                📤 Send Now
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  setSelectedCampaign(campaign);
                  setCampaignForm({ ...campaign });
                  setShowCampaignModal(true);
                }}
              >
                ✏️ Edit
              </button>
            </>
          )}
          {campaign.status === 'scheduled' && (
            <button 
              className="btn btn-sm btn-warning"
              onClick={() => smsService.cancelScheduled(campaign.id).then(fetchData)}
            >
              ❌ Cancel
            </button>
          )}
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setSelectedCampaign(campaign);
              setShowPreviewModal(true);
            }}
          >
            👁️ Preview
          </button>
          <button 
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteCampaign(campaign.id)}
          >
            🗑️
          </button>
        </div>
      </div>
    );
  };

  // Render template card
  const renderTemplateCard = (template) => {
    const category = TEMPLATE_CATEGORIES.find(c => c.id === template.category);
    
    return (
      <div key={template.id} className="template-card">
        <div className="template-header">
          <span className="template-category-icon">{category?.icon || '📄'}</span>
          <div className="template-info">
            <h4 className="template-name">{template.name}</h4>
            <span className="template-category">{category?.name || template.category}</span>
          </div>
        </div>

        <div className="template-content">
          <p>{template.content}</p>
        </div>

        {template.variables?.length > 0 && (
          <div className="template-variables">
            {template.variables.map((v, idx) => (
              <span key={idx} className="variable-tag">{`{{${v}}}`}</span>
            ))}
          </div>
        )}

        <div className="template-actions">
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => {
              setCampaignForm({ ...campaignForm, message: template.content, template_id: template.id });
              setShowCampaignModal(true);
            }}
          >
            📝 Use Template
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => {
              setSelectedTemplate(template);
              setTemplateForm({ ...template });
              setShowTemplateModal(true);
            }}
          >
            ✏️ Edit
          </button>
          <button 
            className="btn btn-sm btn-danger"
            onClick={() => handleDeleteTemplate(template.id)}
          >
            🗑️
          </button>
        </div>
      </div>
    );
  };

  // Render campaign modal
  const renderCampaignModal = () => {
    const { charCount, segments } = calculateSMSMetrics(campaignForm.message);
    const estimatedCost = segments * 0.05 * (campaignForm.recipients_count || 1000);

    return (
      <div className="modal-overlay" onClick={() => setShowCampaignModal(false)}>
        <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <h3>{selectedCampaign ? 'Edit SMS Campaign' : 'Create SMS Campaign'}</h3>
            <button className="btn-icon" onClick={() => setShowCampaignModal(false)}>✕</button>
          </div>
          <div className="modal-body">
            <div className="form-group">
              <label>Campaign Name</label>
              <input
                type="text"
                className="form-control"
                value={campaignForm.name}
                onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })}
                placeholder="e.g., Summer Sale Announcement"
              />
            </div>

            <div className="form-group">
              <label>Message</label>
              <textarea
                className="form-control sms-textarea"
                rows={4}
                value={campaignForm.message}
                onChange={e => setCampaignForm({ ...campaignForm, message: e.target.value })}
                placeholder="Type your SMS message here..."
                maxLength={480}
              />
              <div className="message-counter">
                <span className={charCount > 160 ? 'warning' : ''}>
                  {charCount} / 160 characters
                </span>
                <span>{segments} SMS segment{segments > 1 ? 's' : ''}</span>
              </div>
            </div>

            <div className="personalization-info">
              <h4>Available Variables</h4>
              <div className="variables-list">
                {['{{first_name}}', '{{last_name}}', '{{company}}', '{{order_id}}', '{{discount_code}}'].map(v => (
                  <button
                    key={v}
                    className="variable-btn"
                    onClick={() => setCampaignForm({
                      ...campaignForm,
                      message: campaignForm.message + ' ' + v
                    })}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="form-section">
              <h4>Recipients</h4>
              <div className="recipients-options">
                {[
                  { value: 'all', label: 'All Contacts', count: '10,500' },
                  { value: 'segment', label: 'Specific Segment', count: 'varies' },
                  { value: 'upload', label: 'Upload List', count: 'custom' }
                ].map(opt => (
                  <label 
                    key={opt.value}
                    className={`recipient-option ${campaignForm.recipients_type === opt.value ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="recipients"
                      value={opt.value}
                      checked={campaignForm.recipients_type === opt.value}
                      onChange={e => setCampaignForm({ ...campaignForm, recipients_type: e.target.value })}
                    />
                    <div className="option-content">
                      <span className="option-label">{opt.label}</span>
                      <span className="option-count">{opt.count} contacts</span>
                    </div>
                  </label>
                ))}
              </div>

              {campaignForm.recipients_type === 'segment' && (
                <div className="form-group mt-3">
                  <label>Select Segment</label>
                  <select
                    className="form-control"
                    value={campaignForm.recipients_segment}
                    onChange={e => setCampaignForm({ ...campaignForm, recipients_segment: e.target.value })}
                  >
                    <option value="">Choose a segment...</option>
                    <option value="vip">VIP Customers</option>
                    <option value="new">New Customers (30 days)</option>
                    <option value="inactive">Inactive Customers</option>
                    <option value="cart-abandoners">Cart Abandoners</option>
                  </select>
                </div>
              )}
            </div>

            <div className="form-section">
              <h4>Schedule</h4>
              <div className="schedule-options">
                <label className="schedule-option">
                  <input
                    type="radio"
                    name="schedule"
                    checked={!campaignForm.scheduled_at}
                    onChange={() => setCampaignForm({ ...campaignForm, scheduled_at: '' })}
                  />
                  Send Immediately
                </label>
                <label className="schedule-option">
                  <input
                    type="radio"
                    name="schedule"
                    checked={!!campaignForm.scheduled_at}
                    onChange={() => setCampaignForm({ ...campaignForm, scheduled_at: new Date().toISOString().slice(0, 16) })}
                  />
                  Schedule for Later
                </label>
              </div>

              {campaignForm.scheduled_at && (
                <div className="form-group mt-3">
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={campaignForm.scheduled_at}
                    onChange={e => setCampaignForm({ ...campaignForm, scheduled_at: e.target.value })}
                  />
                </div>
              )}
            </div>

            <div className="cost-estimate">
              <h4>Cost Estimate</h4>
              <div className="estimate-details">
                <div className="estimate-row">
                  <span>Recipients:</span>
                  <span>~{campaignForm.recipients_count || 1000}</span>
                </div>
                <div className="estimate-row">
                  <span>Segments per message:</span>
                  <span>{segments}</span>
                </div>
                <div className="estimate-row">
                  <span>Cost per segment:</span>
                  <span>$0.05</span>
                </div>
                <div className="estimate-row total">
                  <span>Estimated Total:</span>
                  <span>${estimatedCost.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={() => setShowCampaignModal(false)}>
              Cancel
            </button>
            <button 
              className="btn btn-secondary"
              onClick={handleCreateCampaign}
            >
              💾 Save as Draft
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => {
                handleCreateCampaign();
                // Could also trigger send immediately
              }}
              disabled={!campaignForm.name || !campaignForm.message}
            >
              📤 {campaignForm.scheduled_at ? 'Schedule' : 'Send Now'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render template modal
  const renderTemplateModal = () => (
    <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{selectedTemplate ? 'Edit Template' : 'Create Template'}</h3>
          <button className="btn-icon" onClick={() => setShowTemplateModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Template Name</label>
            <input
              type="text"
              className="form-control"
              value={templateForm.name}
              onChange={e => setTemplateForm({ ...templateForm, name: e.target.value })}
              placeholder="e.g., Welcome Message"
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <select
              className="form-control"
              value={templateForm.category}
              onChange={e => setTemplateForm({ ...templateForm, category: e.target.value })}
            >
              {TEMPLATE_CATEGORIES.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Message Content</label>
            <textarea
              className="form-control"
              rows={4}
              value={templateForm.content}
              onChange={e => setTemplateForm({ ...templateForm, content: e.target.value })}
              placeholder="Type your template message..."
            />
            <div className="message-counter">
              <span>{templateForm.content.length} characters</span>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowTemplateModal(false)}>
            Cancel
          </button>
          <button 
            className="btn btn-primary"
            onClick={handleCreateTemplate}
            disabled={!templateForm.name || !templateForm.content}
          >
            💾 Save Template
          </button>
        </div>
      </div>
    </div>
  );

  // Render preview modal
  const renderPreviewModal = () => (
    <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
      <div className="modal-content modal-sm" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>📱 Message Preview</h3>
          <button className="btn-icon" onClick={() => setShowPreviewModal(false)}>✕</button>
        </div>
        <div className="modal-body">
          <div className="phone-preview">
            <div className="phone-frame">
              <div className="phone-notch"></div>
              <div className="phone-screen">
                <div className="sms-bubble">
                  <p>{selectedCampaign?.message?.replace(/\{\{(\w+)\}\}/g, 'John')}</p>
                  <span className="sms-time">Now</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  );

  const navigate = useNavigate();

  if (loading && !campaigns.length) {
    return (
      <div>
        <nav className="breadcrumb">
          <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">SMS Marketing</span>
        </nav>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '3rem' }}>Loading SMS Marketing...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">SMS Marketing</span>
      </nav>

      <div className="card">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '3px solid #EF4444' }}>
          <div>
            <h2 style={{ color: 'var(--gray-900)', fontSize: '1.5rem', fontWeight: 'bold', margin: '0 0 0.25rem 0' }}>
              📱 SMS Marketing
            </h2>
            <p style={{ color: 'var(--gray-600)', margin: 0, fontSize: '0.875rem' }}>
              Create and manage SMS campaigns to reach your customers
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setSelectedTemplate(null);
                setTemplateForm({ name: '', category: 'promotional', content: '', variables: [] });
                setShowTemplateModal(true);
              }}
            >
              📋 New Template
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setSelectedCampaign(null);
                setCampaignForm({
                  name: '',
                  message: '',
                  template_id: '',
                  recipients_type: 'all',
                  recipients_segment: '',
                  scheduled_at: '',
                  personalization: true
                });
                setShowCampaignModal(true);
              }}
            >
              ➕ New Campaign
            </button>
          </div>
        </div>

        {/* Alerts */}
        {error && (
          <div className="alert alert-danger mb-4">
            {error}
            <button className="btn-icon ml-auto" onClick={() => setError(null)}>✕</button>
          </div>
        )}
        {success && (
          <div className="alert alert-success mb-4">
            {success}
            <button className="btn-icon ml-auto" onClick={() => setSuccess(null)}>✕</button>
          </div>
        )}

        {/* Stats */}
        {renderStatsCards()}

        {/* Tabs */}
        <div className="tabs">
          {['campaigns', 'templates', 'analytics', 'opt-out'].map(tab => (
            <button
            key={tab}
            className={`tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'campaigns' && '📤 Campaigns'}
            {tab === 'templates' && '📋 Templates'}
            {tab === 'analytics' && '📊 Analytics'}
            {tab === 'opt-out' && '🚫 Opt-Out List'}
          </button>
        ))}
      </div>

      {/* Campaigns Tab */}
      {activeTab === 'campaigns' && (
        <div className="campaigns-grid">
          {campaigns.length > 0 ? (
            campaigns.map(renderCampaignCard)
          ) : (
            <div className="empty-state">
              <span style={{ fontSize: '4rem' }}>📱</span>
              <h3>No SMS campaigns yet</h3>
              <p className="text-secondary">Create your first SMS campaign to start reaching customers</p>
              <button className="btn btn-primary" onClick={() => setShowCampaignModal(true)}>
                ➕ Create Campaign
              </button>
            </div>
          )}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <>
          <div className="template-categories mb-4">
            {TEMPLATE_CATEGORIES.map(cat => (
              <button key={cat.id} className="category-btn">
                {cat.icon} {cat.name}
              </button>
            ))}
          </div>
          <div className="templates-grid">
            {templates.length > 0 ? (
              templates.map(renderTemplateCard)
            ) : (
              <div className="empty-state">
                <span style={{ fontSize: '4rem' }}>📋</span>
                <h3>No templates yet</h3>
                <p className="text-secondary">Create reusable templates to speed up campaign creation</p>
                <button className="btn btn-primary" onClick={() => setShowTemplateModal(true)}>
                  ➕ Create Template
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="grid-2-col gap-4">
          <div className="card">
            <h3 className="card-title">Delivery Performance</h3>
            <div className="delivery-stats">
              {[
                { label: 'Delivered', value: analyticsData?.delivered || 0, color: '#10b981' },
                { label: 'Pending', value: analyticsData?.pending || 0, color: '#f59e0b' },
                { label: 'Failed', value: analyticsData?.failed || 0, color: '#ef4444' },
                { label: 'Bounced', value: analyticsData?.bounced || 0, color: '#6b7280' }
              ].map(stat => (
                <div key={stat.label} className="delivery-stat">
                  <div className="stat-header">
                    <span className="stat-dot" style={{ background: stat.color }}></span>
                    <span>{stat.label}</span>
                  </div>
                  <div className="stat-value">{stat.value.toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <h3 className="card-title">Engagement Metrics</h3>
            <div className="engagement-stats">
              <div className="engagement-stat">
                <span className="stat-label">Click-Through Rate</span>
                <span className="stat-value">{analyticsData?.click_rate || '0'}%</span>
              </div>
              <div className="engagement-stat">
                <span className="stat-label">Conversion Rate</span>
                <span className="stat-value">{analyticsData?.conversion_rate || '0'}%</span>
              </div>
              <div className="engagement-stat">
                <span className="stat-label">Opt-Out Rate</span>
                <span className="stat-value">{analyticsData?.opt_out_rate || '0'}%</span>
              </div>
              <div className="engagement-stat">
                <span className="stat-label">Response Rate</span>
                <span className="stat-value">{analyticsData?.response_rate || '0'}%</span>
              </div>
            </div>
          </div>

          <div className="card col-span-2">
            <h3 className="card-title">Campaign Performance History</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Campaign</th>
                  <th>Sent</th>
                  <th>Delivered</th>
                  <th>Clicked</th>
                  <th>Conversions</th>
                  <th>Cost</th>
                  <th>ROI</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.filter(c => c.status === 'sent').slice(0, 5).map(campaign => (
                  <tr key={campaign.id}>
                    <td>{campaign.name}</td>
                    <td>{campaign.recipients_count?.toLocaleString() || 0}</td>
                    <td>{campaign.delivered_count?.toLocaleString() || 0}</td>
                    <td>{campaign.clicked_count || 0}</td>
                    <td>{campaign.conversions || 0}</td>
                    <td>${campaign.cost?.toFixed(2) || '0.00'}</td>
                    <td className="text-success">{campaign.roi || '0'}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Opt-Out Tab */}
      {activeTab === 'opt-out' && (
        <div className="card">
          <div className="flex justify-between align-center mb-4">
            <h3 className="card-title mb-0">Opt-Out List</h3>
            <span className="text-secondary">{analyticsData?.opt_out_count || 0} contacts</span>
          </div>
          <p className="text-secondary mb-4">
            These contacts have opted out of receiving SMS messages. They will be automatically excluded from all campaigns.
          </p>
          <div className="opt-out-info">
            <div className="info-item">
              <span className="info-icon">📵</span>
              <div>
                <strong>Automatic Compliance</strong>
                <p>All STOP requests are automatically processed</p>
              </div>
            </div>
            <div className="info-item">
              <span className="info-icon">🔒</span>
              <div>
                <strong>Data Protection</strong>
                <p>Opt-out data is stored securely and never shared</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCampaignModal && renderCampaignModal()}
      {showTemplateModal && renderTemplateModal()}
      {showPreviewModal && renderPreviewModal()}

      <style>{`
        .grid-2-col {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1.5rem;
        }
        .gap-4 {
          gap: 1.5rem;
        }
        .col-span-2 {
          grid-column: span 2;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal-content {
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          max-height: 90vh;
          overflow-y: auto;
          width: 100%;
          max-width: 500px;
          border: 1px solid var(--gray-200);
        }
        .modal-content.modal-lg {
          max-width: 800px;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1rem 1.5rem;
          border-bottom: 1px solid var(--gray-200);
        }
        .modal-header h3 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-body {
          padding: 1.5rem;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 0.75rem;
          padding: 1rem 1.5rem;
          border-top: 1px solid var(--gray-200);
          background: var(--gray-100);
        }
        .stats-grid-4 {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1rem;
          margin-bottom: 1.5rem;
        }
        @media (max-width: 1024px) {
          .stats-grid-4 {
            grid-template-columns: repeat(2, 1fr);
          }
        }
        @media (max-width: 640px) {
          .stats-grid-4 {
            grid-template-columns: 1fr;
          }
        }
        .tabs {
          display: flex;
          gap: 0.5rem;
          border-bottom: 2px solid var(--gray-200);
          margin-bottom: 1.5rem;
        }
        .tab {
          padding: 0.75rem 1.5rem;
          border: none;
          background: none;
          cursor: pointer;
          color: var(--gray-600);
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
        }
        .tab.active {
          color: var(--primary-600);
          border-bottom-color: var(--primary-600);
          font-weight: 600;
        }
        .stat-card {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: var(--gray-50);
          padding: 1.5rem;
          border-radius: var(--radius-lg);
          border: 1px solid var(--gray-200);
        }
        .stat-card.credits {
          background: linear-gradient(135deg, var(--primary-500), var(--primary-600));
          color: white;
          border: none;
        }
        .stat-card.credits .stat-label {
          color: rgba(255,255,255,0.8);
        }
        .stat-icon {
          font-size: 2rem;
        }
        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--gray-900);
        }
        .stat-label {
          font-size: 0.875rem;
          color: var(--gray-600);
        }
        .stat-card.credits .stat-value {
          color: white;
        }
        .campaigns-grid, .templates-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1rem;
        }
        .campaign-card, .template-card {
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          padding: 1.5rem;
          border: 1px solid var(--gray-200);
        }
        .campaign-header, .template-header {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }
        .campaign-info, .template-info {
          flex: 1;
        }
        .campaign-name, .template-name {
          margin: 0;
          font-size: 1rem;
        }
        .campaign-date, .template-category {
          font-size: 0.8rem;
          color: var(--gray-500);
        }
        .campaign-status {
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.75rem;
          color: white;
          text-transform: uppercase;
        }
        .campaign-message, .template-content {
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
        }
        .campaign-message p, .template-content p {
          margin: 0;
          font-size: 0.9rem;
        }
        .message-meta {
          display: flex;
          justify-content: space-between;
          margin-top: 0.5rem;
          font-size: 0.75rem;
          color: var(--gray-500);
        }
        .campaign-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 0.5rem;
          margin-bottom: 1rem;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .campaign-stats .stat {
          text-align: center;
        }
        .campaign-stats .stat-value {
          display: block;
          font-weight: 600;
        }
        .campaign-stats .stat-label {
          font-size: 0.7rem;
          color: var(--gray-500);
        }
        .campaign-actions, .template-actions {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .template-category-icon {
          font-size: 2rem;
        }
        .template-variables {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }
        .variable-tag {
          padding: 0.25rem 0.5rem;
          background: var(--primary-100);
          color: var(--primary-700);
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          font-family: monospace;
        }
        .sms-textarea {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        }
        .message-counter {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: var(--gray-500);
          margin-top: 0.5rem;
        }
        .message-counter .warning {
          color: var(--warning-600);
        }
        .personalization-info {
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
          margin-bottom: 1rem;
        }
        .personalization-info h4 {
          margin: 0 0 0.5rem;
          font-size: 0.875rem;
        }
        .variables-list {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .variable-btn {
          padding: 0.25rem 0.5rem;
          background: var(--gray-100);
          border: 1px solid var(--gray-300);
          border-radius: var(--radius-sm);
          font-size: 0.75rem;
          cursor: pointer;
          font-family: monospace;
          color: var(--gray-700);
        }
        .variable-btn:hover {
          background: var(--primary-50);
          border-color: var(--primary-300);
        }
        .form-section {
          margin-bottom: 1.5rem;
        }
        .form-section h4 {
          margin-bottom: 1rem;
          font-size: 0.9rem;
        }
        .recipients-options {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .recipient-option {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          border: 2px solid var(--gray-200);
          border-radius: var(--radius-sm);
          cursor: pointer;
        }
        .recipient-option.selected {
          border-color: var(--primary-500);
          background: var(--primary-50);
        }
        .option-content {
          display: flex;
          flex-direction: column;
        }
        .option-count {
          font-size: 0.75rem;
          color: var(--gray-500);
        }
        .schedule-options {
          display: flex;
          gap: 2rem;
        }
        .schedule-option {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          cursor: pointer;
        }
        .cost-estimate {
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .cost-estimate h4 {
          margin: 0 0 0.75rem;
        }
        .estimate-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        .estimate-row {
          display: flex;
          justify-content: space-between;
        }
        .estimate-row.total {
          padding-top: 0.5rem;
          border-top: 1px solid var(--gray-300);
          font-weight: 600;
        }
        .phone-preview {
          display: flex;
          justify-content: center;
          padding: 2rem;
        }
        .phone-frame {
          width: 280px;
          height: 400px;
          background: #1f2937;
          border-radius: 30px;
          padding: 10px;
          position: relative;
        }
        .phone-notch {
          width: 80px;
          height: 20px;
          background: #1f2937;
          border-radius: 0 0 10px 10px;
          position: absolute;
          top: 0;
          left: 50%;
          transform: translateX(-50%);
        }
        .phone-screen {
          width: 100%;
          height: 100%;
          background: #ffffff;
          border-radius: 20px;
          padding: 2rem 1rem;
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
        }
        .sms-bubble {
          background: var(--primary-500);
          color: white;
          padding: 0.75rem 1rem;
          border-radius: 18px 18px 4px 18px;
          max-width: 85%;
          margin-left: auto;
        }
        .sms-bubble p {
          margin: 0;
          font-size: 0.9rem;
        }
        .sms-time {
          font-size: 0.7rem;
          opacity: 0.8;
          display: block;
          text-align: right;
          margin-top: 0.25rem;
        }
        .template-categories {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .category-btn {
          padding: 0.5rem 1rem;
          background: var(--gray-100);
          border: 1px solid var(--gray-300);
          border-radius: 20px;
          cursor: pointer;
          color: var(--gray-700);
        }
        .category-btn:hover {
          background: var(--gray-200);
        }
        .delivery-stats {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }
        .delivery-stat {
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .stat-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }
        .stat-dot {
          width: 10px;
          height: 10px;
          border-radius: 50%;
        }
        .engagement-stats {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .engagement-stat {
          display: flex;
          justify-content: space-between;
          padding: 0.75rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .opt-out-info {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }
        .info-item {
          display: flex;
          align-items: flex-start;
          gap: 1rem;
          padding: 1rem;
          background: var(--gray-50);
          border-radius: var(--radius-sm);
        }
        .info-icon {
          font-size: 1.5rem;
        }
        .info-item p {
          margin: 0;
          font-size: 0.875rem;
          color: var(--gray-500);
        }
        .col-span-2 {
          grid-column: span 2;
        }
        .empty-state {
          text-align: center;
          padding: 4rem;
          background: var(--gray-50);
          border-radius: var(--radius-lg);
          grid-column: 1 / -1;
        }
        .empty-state h3 {
          margin: 1rem 0 0.5rem;
        }
      `}</style>
      </div>
    </div>
  );
};

export default SMSMarketing;
